<script lang="ts">
import { CompanyInfo, Security } from '@/modules/common/models';
import SimpleSecuritySearch from '@/modules/manual-loan/components/SimpleSecuritySearch.vue';
import LoanDetailsDialog from '@/modules/open-loans/components/LoanDetailsDialog.vue';
import OpenLoansTable from '@/modules/open-loans/components/OpenLoansTable.vue';
import CounterpartySearch from '@/modules/user-accounts/components/CounterpartySearch.vue';
import { store as vuex } from '@/store/store';
import { BrokerOpenLoan } from '@/modules/common/models';
import { BrokerOpenLoansRequest, ContractStatus, loanStatuses } from '@/utils/api/loans';
import { PropType, computed, shallowRef, watch } from 'vue';
import { LoadSuccessParams, SortModelItem } from 'ag-grid-enterprise';
import logger from '@/modules/common/services/logger.service';
import { useOpenLoansApiService } from '@/modules/open-loans/services/open-loans-api.service';

function stubFn() {
  return;
}

const statusItems = Object.entries(loanStatuses).map(([key, value]) => ({
  status: key,
  label: value.label,
}));
</script>

<script setup lang="ts">
const openLoansApiService = useOpenLoansApiService();

const props = defineProps({
  page: {
    type: Number,
    required: true,
  },
  pageSize: {
    type: Number,
    required: true,
  },
  sort: {
    type: Object as PropType<SortModelItem>,
    required: true,
  },
  omitHeaders: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  security: {
    type: null as unknown as PropType<Security | null>,
    required: true,
  },
  lender: {
    type: null as unknown as PropType<CompanyInfo | null>,
    required: true,
  },
  borrower: {
    type: null as unknown as PropType<CompanyInfo | null>,
    required: true,
  },
  statuses: {
    type: null as unknown as PropType<ContractStatus[] | null>,
    required: true,
  },
  displayId: {
    type: null as unknown as PropType<string | null>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'update:page' | 'update:page-size', value: number): void;
  (e: 'update:sort', value: SortModelItem): void;
  (e: 'update:lender' | 'update:borrower', value: CompanyInfo | null): void;
  (e: 'update:security', value: Security | null): void;
  (e: 'update:statuses', value: ContractStatus[] | null): void;
  (e: 'update:displayId', value: string | null): void;
}>();

const socketEvents = vuex.state.socketEvents;

const selectedItems = shallowRef<BrokerOpenLoan[]>([]);
const detail = shallowRef<{ loan: BrokerOpenLoan; isCorpAction: boolean } | null>(null);

const tableRefresh = shallowRef<(config: { purge: boolean }) => void>(stubFn);
const resetSelection = shallowRef(stubFn);

const localPage = computed({
  get: () => props.page,
  set: (value: number) => emit('update:page', value),
});

const localPageSize = computed({
  get: () => props.pageSize,
  set: (value: number) => emit('update:page-size', value),
});

const localSort = computed({
  get: () => props.sort,
  set: (value: SortModelItem) => emit('update:sort', value),
});

watch(
  [
    () => props.security,
    () => props.lender,
    () => props.borrower,
    () => props.statuses,
    () => props.displayId,
  ],
  () => tableRefresh.value({ purge: true })
);

watch(
  () => socketEvents.openLoans.brokerLoan,
  () => tableRefresh.value({ purge: false })
);

function onReady(config: {
  refresh: (config: { purge: boolean }) => void;
  resetSelection: () => void;
}): void {
  tableRefresh.value = config.refresh;
  resetSelection.value = config.resetSelection;
}

async function queryData(config: {
  page: number;
  pageSize: number;
  sort: SortModelItem;
  signal: AbortSignal;
}): Promise<LoadSuccessParams | undefined> {
  try {
    const params: BrokerOpenLoansRequest = {
      filters: {
        lender: props.lender ? props.lender.companyId : null,
        borrower: props.borrower ? props.borrower.companyId : null,
        cusip: props.security ? props.security.cusip : null,
        statuses: props.statuses ? props.statuses.join(',') : null,
        displayId: props.displayId ? props.displayId.toUpperCase() : null,
      },
      pagination: {
        page: config.page,
        limit: config.pageSize,
      },
      sort: `${config.sort.sort === 'desc' ? '-' : ''}${config.sort.colId}`,
    };
    const res = await openLoansApiService.fetchBrokerOpenLoans(params, config.signal);
    if (!res) return;

    return { rowData: res.items, rowCount: res.total };
  } catch (e) {
    logger.warn(e);
  }
}

function handleViewLoan(detailLoan: BrokerOpenLoan | null): void {
  if (!detailLoan) return;

  detail.value = { loan: detailLoan, isCorpAction: false };
}
</script>

<template>
  <VCard class="flex d-flex flex-column">
    <LoanDetailsDialog
      v-if="detail"
      as-broker
      :initial-tab="detail.isCorpAction ? 'corporateActions' : 'history'"
      :loan-id="detail.loan.id"
      @close-modal="detail = null"
    />

    <VContainer class="py-0" fluid>
      <VRow no-gutters>
        <VCol class="gap-1 d-flex align-center">
          <CounterpartySearch
            class="counterparties ml-8"
            clearable
            include-sponsored
            label="All Lenders"
            side="LENDER"
            :value="lender"
            @change="$emit('update:lender', $event)"
          />
          <CounterpartySearch
            class="counterparties ml-8"
            clearable
            include-sponsored
            label="All Borrowers"
            side="BORROWER"
            :value="borrower"
            @change="$emit('update:borrower', $event)"
          />
          <SimpleSecuritySearch
            class="ml-8"
            clearable
            label="Security"
            :value="security"
            @input="$emit('update:security', $event)"
          />
          <VSelect
            class="status-list ml-8"
            clearable
            deletable-chips
            item-text="label"
            item-value="status"
            :items="statusItems"
            multiple
            placeholder="All Loan Statuses"
            small-chips
            :value="statuses"
            @change="$emit('update:statuses', $event)"
          />
          <VTextField
            class="ml-8"
            clearable
            :label="$t('searchLoanId')"
            prepend-inner-icon="mdi-magnify"
            :value="displayId"
            @input="$emit('update:displayId', $event)"
          />
        </VCol>
      </VRow>
    </VContainer>

    <OpenLoansTable
      class="mt-n4"
      data-test="open-loans-table"
      :omit-headers="omitHeaders"
      :page.sync="localPage"
      :page-size.sync="localPageSize"
      :query-data="queryData"
      :selected-items.sync="selectedItems"
      :show-dropdown-actions="false"
      show-select
      :sort.sync="localSort"
      @ready="onReady"
      @view-loan="handleViewLoan"
    />
  </VCard>
</template>

<style lang="scss" scoped>
.gap-1 {
  gap: 1rem;
}
</style>
